import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  TextField
} from '@mui/material';
import {
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon
} from '@mui/icons-material';
import { BRAND_COLORS, TEXT_COLORS, BACKGROUND_COLORS, BORDER_COLORS } from '../../style/colors.js';
import { formatDate, formatNumber, formatPiedrasForModal } from '../../services/Functions';
import { translateText } from '../../translations/translationService';
import { EXTERNO_PRECIOS } from '../../constants/Constants.js';
import { generateSingleAssignmentPDF } from '../../services/api/pdf/PdfApi.js';
import { validateStonesMeasures, validateWorkOrder } from '../../services/api/pdf/ValidationApi.js';
import { useEffect } from 'react';
import AppDialog from '../Common/ui/AppDialog';
import AppButton from '../Common/ui/AppButton';
import { notify } from '../../services/ui/notify';

const ComponentExternoEngastadorModal = ({ 
  open, 
  onClose, 
  moData, 
  userName, 
  externoType,
  onAssign,
  lang = 'es' 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [fechaAsignacion, setFechaAsignacion] = useState(new Date());
  const generatedByName = useSelector((state) => state.user.name);
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [workOrderResult, setWorkOrderResult] = useState(null);
  const [stoneMeasures, setStoneMeasures] = useState({}); // { "MO": { "piedraCode": "medida" } }
  const [stonePrices, setStonePrices] = useState({}); // { "MO": { "piedraCode": "precio" } }
  const [repairMeasures, setRepairMeasures] = useState({}); // { "MO": [ { numeroPiedras, medida1, medida2, precioManual? } ] }
  const [preciosManuales, setPreciosManuales] = useState({}); // { "MO": { "codigoPiedra": "" } } - solo para override opcional

  // Confirmación (reemplaza window.confirm)
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const confirmResolverRef = useRef(null);

  const askConfirm = (message) => {
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
      setConfirmMessage(message);
      setConfirmVisible(true);
    });
  };

  useEffect(() => {
    if (open && moData) {
      const validateData = async () => {
        setIsValidating(true);
        try {
          // Validar medidas de piedras
          const result = await validateStonesMeasures(moData);
          setValidationResult(result);
          
          if (result) {
            const initialMeasures = {};
            const initialPrices = {};
            Object.keys(result).forEach((mo) => {
              initialMeasures[mo] = {};
              initialPrices[mo] = {};
              Object.keys(result[mo]).forEach((piedraCode) => {
                if (result[mo][piedraCode].valid === false) {
                  initialMeasures[mo][piedraCode] = '';
                }
                if (result[mo][piedraCode].valid === true && result[mo][piedraCode].hasPrice === false) {
                  initialPrices[mo][piedraCode] = '';
                }
              });
            });
            setStoneMeasures(initialMeasures);
            setStonePrices(initialPrices);
          }

          // Validar orden de trabajo
          const workOrderResult = await validateWorkOrder(moData);
          setWorkOrderResult(workOrderResult);
        } catch (error) {
          console.error('Error validando datos:', error);
          setValidationResult(null);
          setWorkOrderResult(null);
        } finally {
          setIsValidating(false);
        }
      };
      
      validateData();
    } else {
      setValidationResult(null);
      setWorkOrderResult(null);
      setStoneMeasures({});
      setStonePrices({});
      setRepairMeasures({});
      setPreciosManuales({});
    }
  }, [open, moData]);

  const getStonesNeedingMeasures = (mo) => {
    if (!validationResult || !validationResult[mo]) return [];
    return Object.keys(validationResult[mo]).filter(
      piedraCode => validationResult[mo][piedraCode].valid === false
    );
  };

  const getStonesNeedingPrices = (mo) => {
    if (!validationResult || !validationResult[mo]) return [];
    return Object.keys(validationResult[mo]).filter(
      piedraCode => validationResult[mo][piedraCode].valid === true && validationResult[mo][piedraCode].hasPrice === false
    );
  };

  const isRepairRowValid = (row) => {
    const numero = Number(row.numeroPiedras);
    const medida1 = Number(row.medida1);
    const medida2 = Number(row.medida2);

    if (Number.isNaN(numero) || Number.isNaN(medida1) || Number.isNaN(medida2)) {
      return false;
    }

    return numero > 0 && medida1 > 0 && medida2 > 0;
  };

  const hasValidRepairDataForMo = (mo) => {
    const rows = repairMeasures[mo];
    if (!rows || rows.length === 0) {
      return false;
    }

    return rows.some((row) => isRepairRowValid(row));
  };

  const handleRepairRowChange = (mo, index, field, value) => {
    setRepairMeasures((prev) => {
      const currentRows =
        prev[mo] && prev[mo].length > 0
          ? [...prev[mo]]
          : [{ numeroPiedras: '', medida1: '', medida2: '', precioManual: '' }];

      currentRows[index] = {
        ...currentRows[index],
        [field]: value
      };

      return {
        ...prev,
        [mo]: currentRows
      };
    });
  };

  const handleAddRepairRow = (mo) => {
    setRepairMeasures((prev) => {
      const currentRows =
        prev[mo] && prev[mo].length > 0
          ? [...prev[mo]]
          : [{ numeroPiedras: '', medida1: '', medida2: '' }];

      currentRows.push({ numeroPiedras: '', medida1: '', medida2: '', precioManual: '' });

      return {
        ...prev,
        [mo]: currentRows
      };
    });
  };

  const handleRemoveRepairRow = (mo, index) => {
    setRepairMeasures((prev) => {
      const currentRows = prev[mo] ? [...prev[mo]] : [];

      if (currentRows.length <= 1) {
        return prev;
      }

      currentRows.splice(index, 1);

      return {
        ...prev,
        [mo]: currentRows
      };
    });
  };

  const allMeasuresFilled = () => {
    if (!validationResult) return true;
    
    for (const mo of Object.keys(validationResult)) {
      const stonesNeedingMeasures = getStonesNeedingMeasures(mo);
      for (const piedraCode of stonesNeedingMeasures) {
        if (!stoneMeasures[mo] || !stoneMeasures[mo][piedraCode] || stoneMeasures[mo][piedraCode].trim() === '') {
          return false;
        }
      }
    }
    return true;
  };

  const allPricesFilled = () => {
    if (!validationResult) return true;
    
    for (const mo of Object.keys(validationResult)) {
      const stonesNeedingPrices = getStonesNeedingPrices(mo);
      for (const piedraCode of stonesNeedingPrices) {
        if (!stonePrices[mo] || !stonePrices[mo][piedraCode] || stonePrices[mo][piedraCode].trim() === '') {
          return false;
        }
      }
    }
    return true;
  };

  const allRequiredDataFilled = () => {
    const baseValid = allMeasuresFilled() && allPricesFilled();

    if (!moData) {
      return baseValid;
    }

    const piedrasForMo = formatPiedrasForModal(moData, EXTERNO_PRECIOS);
    const isRepairWithoutStones =
      moData.mo && moData.mo.toString().startsWith('8') && piedrasForMo.length === 0;

    if (!isRepairWithoutStones) {
      return baseValid;
    }

    return baseValid && hasValidRepairDataForMo(moData.mo);
  };

  const handleMeasureChange = (mo, piedraCode, value) => {
    setStoneMeasures(prev => ({
      ...prev,
      [mo]: {
        ...prev[mo],
        [piedraCode]: value
      }
    }));
  };

  const handlePriceChange = (mo, piedraCode, value) => {
    setStonePrices(prev => ({
      ...prev,
      [mo]: {
        ...prev[mo],
        [piedraCode]: value
      }
    }));
  };

  const handlePrecioManualChange = (mo, codigoPiedra, value) => {
    setPreciosManuales(prev => ({
      ...prev,
      [mo]: {
        ...prev[mo],
        [codigoPiedra]: value
      }
    }));
  };

  const generatePDF = async () => {
    setIsLoading(true);
    try {
      const result = await generateSingleAssignmentPDF(
        userName,
        moData.mo,
        fechaAsignacion instanceof Date ? fechaAsignacion.toISOString().split('T')[0] : fechaAsignacion,
        generatedByName
      );
      
      if (result && result.pdfBlob) {
        const { pdfBlob, filename } = result;
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        notify.error('Error generando PDF. Contacta al administrador.');
      }
    } catch (error) {
      console.error('Error generando PDF:', error);
      notify.error('Error generando PDF. Contacta al administrador.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleConfirmAndGeneratePDF = async () => {
    if (!onAssign) {
      console.warn('Función onAssign no proporcionada');
      return;
    }

    if (!allRequiredDataFilled()) {
      notify.warn('Por favor, completa todas las medidas y precios faltantes antes de asignar.');
      return;
    }

    // Verificar si tiene orden de trabajo
    if (workOrderResult && workOrderResult[moData.mo]) {
      const workOrderNumber = workOrderResult[moData.mo];
      const confirmReplace = await askConfirm(
        `Las MO ${moData.mo} ya tiene una orden de trabajo (${workOrderNumber}), ¿Quiere reemplazarla?`
      );
      if (!confirmReplace) return; // Cancela la operación
    }

    setIsLoading(true);
    try {
      await onAssign([moData], userName);

      const piedrasForMo = formatPiedrasForModal(moData, EXTERNO_PRECIOS);
      const isRepairWithoutStones =
        moData.mo && moData.mo.toString().startsWith('8') && piedrasForMo.length === 0;

      let repairMeasuresPayload = {};

      if (isRepairWithoutStones) {
        const rows = (repairMeasures[moData.mo] || []).filter((row) =>
          isRepairRowValid(row)
        );

        if (rows.length > 0) {
          repairMeasuresPayload = {
            [moData.mo]: rows.map((row) => {
              const line = {
                numeroPiedras: Number(row.numeroPiedras),
                medida1: Number(row.medida1),
                medida2: Number(row.medida2)
              };
              const precio = parseFloat(row.precioManual);
              if (!Number.isNaN(precio) && precio > 0) {
                line.precioManual = precio;
              }
              return line;
            })
          };
        }
      }

      const preciosManualesPayload = {};
      const manualesForMo = preciosManuales[moData.mo];
      if (manualesForMo && typeof manualesForMo === 'object') {
        const filtered = {};
        Object.keys(manualesForMo).forEach(codigo => {
          const val = parseFloat(manualesForMo[codigo]);
          if (!Number.isNaN(val) && val > 0) {
            filtered[codigo] = val;
          }
        });
        if (Object.keys(filtered).length > 0) {
          preciosManualesPayload[moData.mo] = filtered;
        }
      }
      
      const result = await generateSingleAssignmentPDF(
        userName,
        moData.mo,
        fechaAsignacion instanceof Date ? fechaAsignacion.toISOString().split('T')[0] : fechaAsignacion,
        generatedByName,
        stoneMeasures,
        stonePrices,
        repairMeasuresPayload,
        preciosManualesPayload
      );
      
      if (result && result.pdfBlob) {
        const { pdfBlob, filename } = result;
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        notify.error('Error generando PDF. Contacta al administrador.');
      }
      
      handleClose();
    } catch (error) {
      console.error('Error en confirmación o generación de PDF:', error);
      notify.error('Error en el proceso. Contacta al administrador.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!moData) return null;

  const piedras = formatPiedrasForModal(moData, EXTERNO_PRECIOS);
  const isRepairWithoutStones =
    moData.mo && moData.mo.toString().startsWith('8') && piedras.length === 0;
  const repairRowsForMo =
    repairMeasures[moData.mo] && repairMeasures[moData.mo].length > 0
      ? repairMeasures[moData.mo]
      : [{ numeroPiedras: '', medida1: '', medida2: '', precioManual: '' }];

  return (
    <>
      <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: BRAND_COLORS.PRIMARY,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
          px: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AssignmentIcon sx={{ fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {translateText('Asignación a Engastador Externo', lang)}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, backgroundColor: BACKGROUND_COLORS.SECONDARY }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <PersonIcon sx={{ color: BRAND_COLORS.PRIMARY, fontSize: 16 }} />
              <Typography variant="body2" sx={{ color: TEXT_COLORS.PRIMARY, fontWeight: 600 }}>
                {userName}
              </Typography>
            </Box>
            <Chip 
              label="Engastador Externo" 
              size="small" 
              sx={{ 
                backgroundColor: BRAND_COLORS.PRIMARY, 
                color: 'white',
                fontWeight: 500,
                fontSize: '0.75rem'
              }} 
            />
          </Box>
        </Box>

        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1.5, color: TEXT_COLORS.PRIMARY, fontWeight: 600 }}>
            {translateText('Información de la Orden de Manufactura', lang)}
          </Typography>
          
          <TableContainer component={Paper} sx={{ mb: 2, boxShadow: 'none', border: `1px solid ${BORDER_COLORS.LIGHT}` }}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: BACKGROUND_COLORS.SECONDARY }}>
                    {translateText('MO', lang)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {moData.mo}
                      {workOrderResult && workOrderResult[moData.mo] && (
                        <Tooltip title={`Ya tiene orden de trabajo: ${workOrderResult[moData.mo]}`}>
                          <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: BACKGROUND_COLORS.SECONDARY }}>
                    {translateText('PO', lang)}
                  </TableCell>
                  <TableCell>{moData.po}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: BACKGROUND_COLORS.SECONDARY }}>
                    {translateText('Referencia', lang)}
                  </TableCell>
                  <TableCell>{moData.referencia}</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: BACKGROUND_COLORS.SECONDARY }}>
                    {translateText('Sub-Referencia', lang)}
                  </TableCell>
                  <TableCell>{moData.sureferencia}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: BACKGROUND_COLORS.SECONDARY }}>
                    {translateText('Cantidad', lang)}
                  </TableCell>
                  <TableCell>{formatNumber(moData.cantidad)}</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: BACKGROUND_COLORS.SECONDARY }}>
                    {translateText('Fecha de Asignación', lang)}
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      value={fechaAsignacion instanceof Date ? fechaAsignacion.toISOString().split('T')[0] : fechaAsignacion}
                      onChange={(e) => setFechaAsignacion(new Date(e.target.value))}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        '& .MuiOutlinedInput-root': { 
                          height: '32px',
                          fontSize: '0.875rem'
                        }
                      }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: BACKGROUND_COLORS.SECONDARY }}>
                    {translateText('Fecha Entrega Cliente', lang)}
                  </TableCell>
                  <TableCell>{formatDate(moData.fechaentrega)}</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: BACKGROUND_COLORS.SECONDARY }}>
                    {translateText('Stamping', lang)}
                  </TableCell>
                  <TableCell>{moData.stamping}</TableCell>
                </TableRow>
                {moData.observaciones && moData.observaciones.trim() !== ' ' && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: BACKGROUND_COLORS.SECONDARY }}>
                      {translateText('Observaciones', lang)}
                    </TableCell>
                    <TableCell colSpan={3}>{moData.observaciones}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle1" sx={{ mb: 1.5, color: TEXT_COLORS.PRIMARY, fontWeight: 600 }}>
            {translateText('Lista de Piedras', lang)}
          </Typography>
          
          {piedras.length > 0 ? (
            <TableContainer component={Paper} sx={{ mb: 2, boxShadow: 'none', border: `1px solid ${BORDER_COLORS.LIGHT}` }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: BACKGROUND_COLORS.SECONDARY }}>
                      {translateText('Código Piedra', lang)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: BACKGROUND_COLORS.SECONDARY, textAlign: 'center' }}>
                      {translateText('Cantidad', lang)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: BACKGROUND_COLORS.SECONDARY, textAlign: 'right' }}>
                      {translateText('Precio manual (opcional)', lang)}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {piedras.map((piedra, index) => {
                    const needsMeasure = validationResult && 
                                      validationResult[moData.mo] && 
                                      validationResult[moData.mo][piedra.codigo] &&
                                      validationResult[moData.mo][piedra.codigo].valid === false;
                    
                    const needsPrice = validationResult && 
                                    validationResult[moData.mo] && 
                                    validationResult[moData.mo][piedra.codigo] &&
                                    validationResult[moData.mo][piedra.codigo].valid === true &&
                                    validationResult[moData.mo][piedra.codigo].hasPrice === false;
                    
                    const minMeasure = needsPrice && validationResult[moData.mo][piedra.codigo].minMeasure;
                    
                    return (
                      <React.Fragment key={piedra.codigo}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500, color: BRAND_COLORS.PRIMARY }}>
                            {piedra.codigo}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 500 }}>
                            {piedra.cantidad}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle', textAlign: 'right' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <TextField
                                type="number"
                                size="small"
                                placeholder="—"
                                value={preciosManuales[moData.mo]?.[piedra.codigo] ?? ''}
                                onChange={(e) => handlePrecioManualChange(moData.mo, piedra.codigo, e.target.value)}
                                sx={{
                                  width: 88,
                                  '& .MuiOutlinedInput-root': { height: 30 },
                                  '& .MuiOutlinedInput-input': { fontSize: '0.8rem', padding: '4px 8px' },
                                  '& input[type=number]': { MozAppearance: 'textfield' },
                                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none', margin: 0
                                  }
                                }}
                                inputProps={{ min: 0, step: '0.01' }}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                        {needsMeasure && (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ py: 1, backgroundColor: '#fffbf0' }}>
                              <Box>
                                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#856404', mb: 0.5, display: 'block', fontWeight: 600 }}>
                                  Medida para {piedra.codigo}:
                                </Typography>
                                <TextField
                                  size="small"
                                  fullWidth
                                  placeholder=""
                                  value={stoneMeasures[moData.mo]?.[piedra.codigo] || ''}
                                  onChange={(e) => handleMeasureChange(moData.mo, piedra.codigo, e.target.value)}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      height: '36px',
                                      backgroundColor: '#fff3cd'
                                    }
                                  }}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                        {needsPrice && (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ py: 1, backgroundColor: '#fffbf0' }}>
                              <Box>
                                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#856404', mb: 0.5, display: 'block', fontWeight: 600 }}>
                                  Precio para {piedra.codigo}{minMeasure ? `, ${minMeasure} mm` : ''}:
                                </Typography>
                                <TextField
                                  size="small"
                                  fullWidth
                                  type="number"
                                  placeholder=""
                                  value={stonePrices[moData.mo]?.[piedra.codigo] || ''}
                                  onChange={(e) => handlePriceChange(moData.mo, piedra.codigo, e.target.value)}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      height: '36px',
                                      backgroundColor: '#fff3cd'
                                    }
                                  }}
                                  inputProps={{
                                    step: '0.01',
                                    min: '0'
                                  }}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                backgroundColor: BACKGROUND_COLORS.SECONDARY,
                borderRadius: 2,
                border: `1px solid ${BORDER_COLORS.LIGHT}`,
                mb: 3
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: TEXT_COLORS.SECONDARY, fontStyle: 'italic', mb: isRepairWithoutStones ? 2 : 0 }}
              >
                {translateText('No hay piedras registradas para esta MO', lang)}
              </Typography>

              {isRepairWithoutStones && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.75, fontWeight: 600, color: TEXT_COLORS.PRIMARY, fontSize: '0.8rem' }}
                  >
                    {translateText('Datos manuales para reparación', lang)}
                  </Typography>

                  {repairRowsForMo.map((row, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        mb: 1,
                        flexWrap: 'nowrap'
                      }}
                    >
                      <TextField
                        label={translateText('Nº piedras', lang)}
                        type="number"
                        size="small"
                        value={row.numeroPiedras}
                        onChange={(e) =>
                          handleRepairRowChange(moData.mo, index, 'numeroPiedras', e.target.value)
                        }
                        sx={{
                          width: 88,
                          '& .MuiOutlinedInput-root': { height: 32 },
                          '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                          '& .MuiOutlinedInput-input': { fontSize: '0.75rem', padding: '6px 8px' },
                          '& input[type=number]': { MozAppearance: 'textfield' },
                          '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none', margin: 0
                          }
                        }}
                        inputProps={{ min: 0 }}
                      />
                      <TextField
                        label={translateText('1ª mm', lang)}
                        type="number"
                        size="small"
                        value={row.medida1}
                        onChange={(e) =>
                          handleRepairRowChange(moData.mo, index, 'medida1', e.target.value)
                        }
                        sx={{
                          width: 72,
                          '& .MuiOutlinedInput-root': { height: 32 },
                          '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                          '& .MuiOutlinedInput-input': { fontSize: '0.75rem', padding: '6px 8px' },
                          '& input[type=number]': { MozAppearance: 'textfield' },
                          '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none', margin: 0
                          }
                        }}
                        inputProps={{ min: 0, step: '0.01' }}
                      />
                      <TextField
                        label={translateText('2ª mm', lang)}
                        type="number"
                        size="small"
                        value={row.medida2}
                        onChange={(e) =>
                          handleRepairRowChange(moData.mo, index, 'medida2', e.target.value)
                        }
                        sx={{
                          width: 72,
                          '& .MuiOutlinedInput-root': { height: 32 },
                          '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                          '& .MuiOutlinedInput-input': { fontSize: '0.75rem', padding: '6px 8px' },
                          '& input[type=number]': { MozAppearance: 'textfield' },
                          '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none', margin: 0
                          }
                        }}
                        inputProps={{ min: 0, step: '0.01' }}
                      />
                      <TextField
                        label={translateText('Precio manual', lang)}
                        type="number"
                        size="small"
                        value={row.precioManual ?? ''}
                        onChange={(e) =>
                          handleRepairRowChange(moData.mo, index, 'precioManual', e.target.value)
                        }
                        sx={{
                          width: 88,
                          '& .MuiOutlinedInput-root': { height: 32 },
                          '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                          '& .MuiOutlinedInput-input': { fontSize: '0.75rem', padding: '6px 8px' },
                          '& input[type=number]': { MozAppearance: 'textfield' },
                          '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none', margin: 0
                          }
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                      />

                      {repairRowsForMo.length > 1 && (
                        <IconButton
                          aria-label="remove-repair-row"
                          onClick={() => handleRemoveRepairRow(moData.mo, index)}
                          size="small"
                          sx={{ p: 0.25 }}
                        >
                          <RemoveCircleOutlineIcon color="error" fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}

                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.5 }}>
                    <Button
                      startIcon={<AddCircleOutlineIcon sx={{ fontSize: '1rem' }} />}
                      size="small"
                      variant="text"
                      onClick={() => handleAddRepairRow(moData.mo)}
                      sx={{ fontSize: '0.75rem', textTransform: 'none', p: 0.25, minWidth: 0 }}
                    >
                      {translateText('Añadir línea', lang)}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {piedras.length > 0 && (
            <Box sx={{ 
              p: 1.5, 
              backgroundColor: 'rgba(70, 93, 100, 0.1)', 
              borderRadius: 2,
              border: '1px solid rgba(70, 93, 100, 0.3)',
              mb: 2
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: BRAND_COLORS.PRIMARY }}>
                  {translateText('Total Piedras:', lang)}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND_COLORS.PRIMARY }}>
                  {piedras.reduce((total, piedra) => total + piedra.cantidad, 0)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>


        {isValidating && (
          <Box sx={{ mt: 2, p: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: TEXT_COLORS.SECONDARY }}>
              Validando piedras...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: BACKGROUND_COLORS.SECONDARY }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderColor: BORDER_COLORS.MEDIUM,
              color: TEXT_COLORS.SECONDARY,
              '&:hover': {
                borderColor: BRAND_COLORS.PRIMARY,
                color: BRAND_COLORS.PRIMARY
              }
            }}
          >
            {translateText('Cancelar', lang)}
          </Button>
          <Button
            onClick={handleConfirmAndGeneratePDF}
            variant="contained"
            disabled={isLoading || isValidating || !allRequiredDataFilled()}
            sx={{
              backgroundColor: BRAND_COLORS.PRIMARY,
              '&:hover': {
                backgroundColor: BRAND_COLORS.PRIMARY_DARK
              }
            }}
          >
            {isLoading 
              ? translateText('Asignando y generando PDF...', lang) 
              : translateText('Confirmar Asignación y Descargar PDF', lang)
            }
          </Button>
        </Box>
      </DialogActions>
      </Dialog>

      <AppDialog
        open={confirmVisible}
        onClose={() => {
          setConfirmVisible(false);
          confirmResolverRef.current?.(false);
        }}
        title='Confirmación'
        maxWidth='xs'
        actions={
          <>
            <AppButton
              onClick={() => {
                setConfirmVisible(false);
                confirmResolverRef.current?.(false);
              }}
              variantType='secondary'>
              Cancelar
            </AppButton>
            <AppButton
              onClick={() => {
                setConfirmVisible(false);
                confirmResolverRef.current?.(true);
              }}>
              Aceptar
            </AppButton>
          </>
        }>
        <Typography>{confirmMessage}</Typography>
      </AppDialog>
    </>
  );
};

export default ComponentExternoEngastadorModal;
